import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import {
    BRAND_LUXE_GOLD,
    BRAND_LUXE_MUTED,
    BRAND_LUXE_PRIMARY,
    BRAND_ON_PRIMARY_FIXED,
    BRAND_PRIMARY_CONTAINER,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
} from '../brandDashboardTokens';
import { activeBrandDashboardBrandSelector } from '../../../redux/buzzbridge/selectors';
import { acceptBrandOpsInvite, fetchBrandOpsAccess, fetchMyBrandMemberships } from '../../../redux/brandOps/actions';
import {
    brandOpsApiUnavailableSelector,
    brandOpsFetchingAccessSelector,
    brandOpsHasContentManagerSelector,
    brandOpsAccessSelector,
} from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { buildContractSignRedirect, getBrandOpsInviteToken } from './manageDeepLinkUtils';

const CURRENCY_SYMBOLS: Record<string, string> = {
    usd: '$',
    ngn: '₦',
    gbp: '£',
    eur: '€',
};

export function fmtCurrency(fee: number | null | undefined, currency?: string | null): string {
    if (fee == null) return '—';
    const sym = CURRENCY_SYMBOLS[(currency || 'usd').toLowerCase()] ?? currency?.toUpperCase() + ' ';
    return `${sym}${Number(fee).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
}

export const MANAGE_PAGE_SX = {
    maxWidth: 1100,
    mx: 'auto' as const,
    py: { xs: 2, md: 3 },
    px: { xs: 0.5, md: 0 },
};

export function ManagePageHeader({
    title,
    subtitle,
    action,
    back,
}: {
    title: string;
    subtitle?: string;
    action?: React.ReactNode;
    back?: string;
}) {
    return (
        <Box sx={{ mb: 3 }}>
            {back ? (
                <Box
                    component={RouterLink}
                    to={back}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: BRAND_LUXE_MUTED,
                        textDecoration: 'none',
                        fontSize: 13,
                        mb: 1.5,
                        '&:hover': { color: '#fff' },
                    }}>
                    <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
                </Box>
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 22, md: 28 },
                            color: '#fff',
                            letterSpacing: '-0.02em',
                        }}>
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography
                            sx={{ mt: 0.5, color: BRAND_LUXE_MUTED, fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13 }}>
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
                {action}
            </Box>
        </Box>
    );
}

/** Polymorphic MUI button (supports `component` / `to` / `label` wrappers). */
export function ManageGoldButton(props: any) {
    const { sx, ...rest } = props;
    return (
        <Button
            {...rest}
            sx={{
                bgcolor: BRAND_PRIMARY_CONTAINER,
                color: BRAND_ON_PRIMARY_FIXED,
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 13,
                textTransform: 'none',
                borderRadius: 999,
                px: 2.5,
                '&:hover': { bgcolor: BRAND_LUXE_GOLD },
                ...sx,
            }}
        />
    );
}

export function ManageSurface({ children, sx }: { children: React.ReactNode; sx?: object }) {
    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER,
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 3,
                p: { xs: 2, md: 2.5 },
                ...sx,
            }}>
            {children}
        </Box>
    );
}

export function StatusChip({ label, tone = 'muted' }: { label: string; tone?: 'gold' | 'mint' | 'muted' | 'error' }) {
    const colors = {
        gold: { color: BRAND_LUXE_PRIMARY, bg: 'rgba(255,201,101,0.12)' },
        mint: { color: '#00fdc1', bg: 'rgba(0,253,193,0.12)' },
        muted: { color: BRAND_LUXE_MUTED, bg: BRAND_SURFACE_CONTAINER_HIGH },
        error: { color: '#ff7351', bg: 'rgba(255,115,81,0.12)' },
    }[tone];
    return (
        <Chip
            size="small"
            label={label}
            sx={{
                height: 24,
                fontSize: 11,
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                color: colors.color,
                bgcolor: colors.bg,
            }}
        />
    );
}

/** Accept invite deep links: `brand_ops_invite` (canonical) or legacy `brandOpsInvite` */
export function useBrandOpsInviteDeepLink(brandId?: string | null): { accepting: boolean } {
    const dispatchP = useDispatchPromise();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const inviteToken = getBrandOpsInviteToken(searchParams);
    const contractSign = searchParams.get('contractSign');
    // Tracks which token has already been dispatched — prevents double-fire when
    // brandId transitions from undefined → real value (two effect runs, one token).
    const processingTokenRef = useRef<string | null>(null);
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        if (!inviteToken) return;
        if (processingTokenRef.current === inviteToken) return;
        processingTokenRef.current = inviteToken;

        let cancelled = false;
        setAccepting(true);
        dispatchP(acceptBrandOpsInvite({ token: inviteToken, brandId: brandId || undefined }))
            .then(() => {
                if (!cancelled) showToastMessage('Invite accepted', 'success');
            })
            .catch((err: any) => {
                if (!cancelled) {
                    showToastMessage(err?.message || 'Could not accept invite', 'error');
                }
            })
            .finally(() => {
                // Always clear the loader — even if the effect was "cancelled" by a
                // deps change mid-flight. Without this the overlay gets stuck on screen.
                setAccepting(false);
                if (cancelled) return;
                setSearchParams(
                    prev => {
                        const next = new URLSearchParams(prev);
                        next.delete('brand_ops_invite');
                        next.delete('brandOpsInvite');
                        return next;
                    },
                    { replace: true },
                );
            });
        return () => {
            cancelled = true;
        };
    }, [inviteToken, brandId, dispatchP, setSearchParams]);

    /** Legacy `?contractSign=&brandId=` → canonical sign route */
    useEffect(() => {
        if (!contractSign) return;
        const target = buildContractSignRedirect(ROUTES.BRAND_MANAGE_CONTRACT_SIGN, searchParams);
        if (target) navigate(target, { replace: true });
    }, [contractSign, searchParams, navigate]);

    return { accepting };
}

export function useBrandManageGate() {
    const dispatch = useDispatch();
    const brand = useSelector(activeBrandDashboardBrandSelector);
    const brandId = brand?._id;
    const hasContentManager = useSelector(brandOpsHasContentManagerSelector);
    const access = useSelector(brandOpsAccessSelector);
    const fetching = useSelector(brandOpsFetchingAccessSelector);
    const apiUnavailable = useSelector(brandOpsApiUnavailableSelector);

    // If the user has no owned brand, check whether they're a team member of
    // another brand (invited manager). The saga resolves the brand and sets it active.
    const membershipFetchedRef = useRef(false);
    useEffect(() => {
        if (!brandId && !membershipFetchedRef.current) {
            membershipFetchedRef.current = true;
            dispatch(fetchMyBrandMemberships());
        }
    }, [brandId, dispatch]);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsAccess({ brandId }));
    }, [dispatch, brandId]);

    return { brand, brandId, hasContentManager, access, fetching, apiUnavailable };
}

export function BrandManageGate({
    children,
    requireEntitlement = true,
}: {
    children: React.ReactNode;
    requireEntitlement?: boolean;
}) {
    const { brand, fetching, apiUnavailable, access } = useBrandManageGate();
    const brandId = brand?._id;
    // useBrandOpsInviteDeepLink is called ONLY here — never in useBrandManageGate —
    // so there is exactly one instance and the accept endpoint is called exactly once.
    const { accepting } = useBrandOpsInviteDeepLink(brandId);

    // While accepting an invite the brand may not yet be in state — show full
    // screen loader and block all other gate checks until accept completes.
    if (accepting) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 1400,
                    bgcolor: 'rgba(0,0,0,0.72)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                }}>
                <CircularProgress size={40} sx={{ color: BRAND_LUXE_PRIMARY }} />
                <Typography sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14 }}>
                    Accepting invite…
                </Typography>
            </Box>
        );
    }

    if (!brand) {
        return (
            <Box sx={{ ...MANAGE_PAGE_SX, textAlign: 'center', py: 8 }}>
                <Typography sx={{ color: BRAND_LUXE_MUTED, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    Select or create a brand to use Brand Manage.
                </Typography>
            </Box>
        );
    }

    if (fetching && !access) {
        return (
            <Box sx={{ ...MANAGE_PAGE_SX, display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: BRAND_LUXE_PRIMARY }} />
            </Box>
        );
    }

    if (apiUnavailable) {
        return (
            <Box sx={MANAGE_PAGE_SX}>
                <Alert
                    severity="info"
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.08)',
                        '& .MuiAlert-icon': { color: BRAND_LUXE_PRIMARY },
                    }}>
                    Brand Manage is not available yet. Check back when your workspace is enabled.
                </Alert>
            </Box>
        );
    }

    // For invited managers the backend sets can_access=true regardless of the
    // owner's entitlement (they can't subscribe on the owner's behalf).
    // Only block when can_access is explicitly false (owner without entitlement).
    if (requireEntitlement && access?.can_access === false) {
        return (
            <Box sx={MANAGE_PAGE_SX}>
                <ManageSurface>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 18, mb: 1 }}>
                        Content Manager required
                    </Typography>
                    <Typography
                        sx={{ color: BRAND_LUXE_MUTED, fontSize: 13, mb: 2, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        {access?.message ||
                            'Upgrade your host plan with the Content Manager entitlement to unlock Team, Contracts, Calendar, and Social tools.'}
                    </Typography>
                    <ManageGoldButton component={RouterLink as any} to={ROUTES.BRAND_SETTINGS}>
                        View plans
                    </ManageGoldButton>
                </ManageSurface>
            </Box>
        );
    }

    return <>{children}</>;
}

export function ComingSoonStub({ title, blurb }: { title: string; blurb: string }) {
    return (
        <BrandManageGate requireEntitlement={false}>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader title={title} subtitle={blurb} />
                <ManageSurface sx={{ textAlign: 'center', py: 6 }}>
                    <Typography
                        sx={{ color: BRAND_LUXE_PRIMARY, fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, mb: 1 }}>
                        Coming soon
                    </Typography>
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        This module lands in Phase 2.
                    </Typography>
                    <Button
                        component={RouterLink}
                        to={ROUTES.BRAND_MANAGE}
                        sx={{ mt: 2, color: BRAND_LUXE_PRIMARY, textTransform: 'none' }}>
                        Back to Manage
                    </Button>
                </ManageSurface>
            </Box>
        </BrandManageGate>
    );
}
